import { useEffect, useRef } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getVoicesEngage,
  getVoicesResults,
  getVoicesSyncStatus,
  getVoicesToday,
  startVoicesSync,
} from "../Api/voicesOverview";
import {
  VoicesEngage,
  VoicesSyncKind,
  VoicesSyncStatus,
  VoicesToday,
} from "../Models/voicesOverview";
import { VoicesResults, VoicesResultsFilters } from "../Models/voicesResults";

const TODAY_POLL_MS = 60000;

export const useVoicesToday = (enabled = true) => {
  return useQuery<VoicesToday, Error>({
    queryKey: ["voicesToday"],
    queryFn: () => getVoicesToday(),
    enabled,
    refetchInterval: TODAY_POLL_MS,
  });
};

export const useVoicesEngage = (limit: number) => {
  return useQuery<VoicesEngage, Error>({
    queryKey: ["voicesEngage", limit],
    queryFn: () => getVoicesEngage(limit),
    placeholderData: keepPreviousData,
  });
};

export const useVoicesResults = (filters: VoicesResultsFilters, enabled: boolean) => {
  return useQuery<VoicesResults, Error>({
    queryKey: ["voicesResults", filters],
    queryFn: () => getVoicesResults(filters),
    enabled,
  });
};

const SYNC_POLL_MS = 3000;
const SYNCED_QUERIES = [
  "voicesResults",
  "voicesPosts",
  "voicesPost",
  "voicesToday",
  "voicesCalendar",
  "voicesEngage",
];

/** Syncing the workspace with Buffer; the lists refresh when a sync finishes. */
export const useVoicesSync = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const status = useQuery<VoicesSyncStatus, Error>({
    queryKey: ["voicesSync"],
    queryFn: () => getVoicesSyncStatus(),
    enabled,
    refetchInterval: (query) => (query.state.data?.running ? SYNC_POLL_MS : false),
  });
  const running = !!status.data?.running;
  const wasRunning = useRef(running);

  useEffect(() => {
    if (wasRunning.current && !running) {
      SYNCED_QUERIES.forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
    }
    wasRunning.current = running;
  }, [running, queryClient]);

  const start = useMutation({
    mutationFn: (variables: { kind: VoicesSyncKind; manual: boolean }) =>
      startVoicesSync(variables.kind, variables.manual),
    onSuccess: (data) => queryClient.setQueryData(["voicesSync"], data),
  });

  return { status: status.data, start };
};
